/**
 * Utilitas alamat berkas media.
 *
 * MASALAH YANG DITANGANI
 * Basis data menyimpan jalur relatif seperti
 *   uploads/tournament_images/z5FhEdir....jpg
 * Berkas fisiknya berada di server produksi, di dalam storage/app/public.
 * Di komputer lokal folder itu kosong (memang di-.gitignore), sehingga setiap
 * gambar turnamen menghasilkan 404 dan tampil sebagai ikon rusak.
 *
 * Jalur yang tersimpan juga tidak seragam: sebagian sudah berupa URL lengkap,
 * sebagian diawali garis miring, sebagian sudah memuat awalan "storage/".
 * Fungsi di bawah menyeragamkan semuanya, lalu menyediakan gambar pengganti
 * bila berkas aslinya tidak dapat dimuat.
 */

const API_BASE = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api';

/** Alamat asal server, tanpa akhiran /api */
export const SERVER_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

/**
 * Foto gelanggang untuk dipakai saat gambar turnamen tidak tersedia.
 *
 * Berkas ini berasal dari foto pertandingan asli yang sudah ada di proyek
 * (src/assets/images/main/banner/1..5.png), dipotong ke rasio 16:9 dan
 * dikompres: dari 13,25 MB menjadi 714 KB untuk kelimanya.
 *
 * Poster acara seperti "Bogor Pencak Silat Series 1" sengaja TIDAK dipakai —
 * poster memuat tanggal, tempat, dan nama kejuaraan tertentu, sehingga
 * menempelkannya pada turnamen lain justru menyampaikan informasi yang keliru.
 * Foto suasana gelanggang tidak terikat pada satu acara.
 */
const FALLBACKS = [
  require('@/assets/brand/fallback/arena-1.jpg'),
  require('@/assets/brand/fallback/arena-2.jpg'),
  require('@/assets/brand/fallback/arena-3.jpg'),
  require('@/assets/brand/fallback/arena-4.jpg'),
  require('@/assets/brand/fallback/arena-5.jpg'),
];

/**
 * Menyusun URL lengkap dari jalur berkas yang tersimpan di basis data.
 * Mengembalikan null bila tidak ada jalur, supaya komponen bisa langsung
 * menampilkan pengganti alih-alih meminta berkas kosong.
 */
export function mediaUrl(path) {
  if (!path || typeof path !== 'string') return null;

  const bersih = path.trim();
  if (!bersih) return null;

  if (/^(https?:)?\/\//i.test(bersih) || bersih.startsWith('data:')) {
    return bersih;
  }

  // Buang garis miring dan awalan "storage/" yang kadang ikut tersimpan,
  // supaya tidak menghasilkan ".../storage/storage/uploads/..."
  const relatif = bersih.replace(/^\/+/, '').replace(/^storage\//, '');

  return `${SERVER_ORIGIN}/storage/${relatif}`;
}

/**
 * Memilih foto pengganti secara tetap berdasarkan teks kunci.
 *
 * Pemilihan sengaja TIDAK acak: dengan hash sederhana, satu turnamen selalu
 * mendapat foto yang sama di setiap pemuatan halaman. Kalau dipilih acak,
 * gambar akan berubah-ubah setiap kali komponen dirender ulang dan itu
 * terlihat seperti kerusakan.
 */
export function fallbackImage(kunci = '') {
  const teks = String(kunci);
  let hash = 0;
  for (let i = 0; i < teks.length; i += 1) {
    hash = (hash * 31 + teks.charCodeAt(i)) % 100000;
  }
  return FALLBACKS[hash % FALLBACKS.length];
}

/**
 * Pengganti terakhir berupa SVG data-URI, dipakai hanya bila foto pengganti
 * pun gagal dimuat.
 *
 * Berbentuk data-URI, bukan berkas terpisah, supaya tidak menimbulkan
 * permintaan jaringan tambahan dan mustahil ikut gagal — pengganti yang juga
 * rusak adalah kondisi yang paling membingungkan saat menelusuri galat.
 */
export function placeholderImage(label = '') {
  const teks = String(label).slice(0, 26).replace(/[<>&"']/g, '');

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="800" height="450" viewBox="0 0 800 450">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#B01008"/>
      <stop offset="45%" stop-color="#E84808"/>
      <stop offset="100%" stop-color="#F8A808"/>
    </linearGradient>
  </defs>
  <rect width="800" height="450" fill="url(#g)"/>
  <g fill="none" stroke="#fff" stroke-opacity="0.16" stroke-width="2">
    <path d="M-40 340 L200 170 L420 270 L620 120 L860 230"/>
  </g>
  <g transform="translate(400 195)" fill="#fff" fill-opacity="0.92">
    <path d="M-42 -28 h84 a6 6 0 0 1 6 6 v9 a31 31 0 0 1 -31 31 h-34 a31 31 0 0 1 -31 -31 v-9 a6 6 0 0 1 6 -6 z"/>
    <rect x="-8" y="18" width="16" height="24" rx="3"/>
    <rect x="-26" y="42" width="52" height="10" rx="4"/>
  </g>
  ${
    teks
      ? `<text x="400" y="300" text-anchor="middle" fill="#fff" fill-opacity="0.95"
            font-family="Segoe UI, Roboto, sans-serif" font-size="27" font-weight="700">${teks}</text>`
      : ''
  }
  <text x="400" y="${teks ? 334 : 306}" text-anchor="middle" fill="#fff" fill-opacity="0.6"
        font-family="Segoe UI, Roboto, sans-serif" font-size="14" font-weight="600"
        letter-spacing="3">ARENA SPORT DIGITAL</text>
</svg>`.trim();

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export default { mediaUrl, fallbackImage, placeholderImage, SERVER_ORIGIN };
